@file:OptIn(kotlinx.serialization.ExperimentalSerializationApi::class)

package ds.pms.tokafull.adminweb

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonNames

@Serializable
data class PricingMatrixRowDto(
    val productId: String,
    val productName: String,
    val productNameUr: String? = null,
    val baseSku: String,
    val st: String? = null,
    val c: String? = null,
    val d: String? = null,
    val gen: String? = null,
    val n: String? = null,
    val s: String? = null,
    val t: String? = null
)

@Serializable
data class UpdateMatrixCellDto(
    val productId: String,
    val brandCode: String,
    val basePrice: String,
    val allowZeroOverride: Boolean = false
)

@Serializable
data class UpdateMatrixRequestDto(val cells: List<UpdateMatrixCellDto>)

@Serializable
data class UpdateMatrixResponseDto(val updatedRows: Int)

@Serializable
data class AdminProductSummaryDto(
    @JsonNames("product_id", "id")
    val productId: String,
    @JsonNames("product_name")
    val productName: String,
    @JsonNames("product_name_ur")
    val productNameUr: String? = null,
    @JsonNames("line_name")
    val lineName: String,
    @JsonNames("section_name")
    val sectionName: String,
    @JsonNames("product_line_id")
    val productLineId: String = "",
    @JsonNames("catalog_section_id")
    val catalogSectionId: String = "",
    @JsonNames("variant_count")
    val variantCount: Int,
    val status: String,
    @JsonNames("image_url")
    val imageUrl: String? = null,
)

@Serializable
data class ProductImageUploadResponseDto(
    @JsonNames("image_url")
    val imageUrl: String,
)

@Serializable
data class AdminProductImageClearRequestDto(
    @JsonNames("product_id")
    val productId: String,
)

@Serializable
data class PagedProductsDto(
    val items: List<AdminProductSummaryDto>,
    val total: Long,
    val page: Int,
    val pageSize: Int
)

@Serializable
data class PagedMatrixDto(
    val items: List<PricingMatrixRowDto>,
    val total: Long,
    val page: Int,
    val pageSize: Int
)

@Serializable
data class ModifierRowDto(
    val id: String,
    val attrKey: String,
    val attrValue: String,
    val attrValueUr: String? = null,
    val priceImpact: String
)

@Serializable
data class PagedModifiersDto(
    val items: List<ModifierRowDto>,
    val total: Long,
    val page: Int,
    val pageSize: Int
)

/** Result of GET /admin/modifiers (includes flag when API returns 404 — old server image). */
data class ModifiersPageLoad(
    val page: PagedModifiersDto,
    val endpointMissing: Boolean = false
)

@Serializable
data class AdminUserRowDto(
    val id: String,
    val username: String,
    @JsonNames("created_at") val createdAt: Long,
    @JsonNames("name_en") val nameEn: String? = null,
    @JsonNames("name_ur") val nameUr: String? = null,
    @JsonNames("address_en") val addressEn: String? = null,
    @JsonNames("mobile_en") val mobileEn: String? = null,
    /** Present on current API; omitted on older images — defaults avoid decode failure. */
    @JsonNames("user_type") val userType: String = "admin",
    val status: String = "active",
    @JsonNames("brand_codes") val brandCodes: List<String> = emptyList(),
)

@Serializable
data class PagedUsersDto(
    val items: List<AdminUserRowDto>,
    val total: Long,
    val page: Int,
    @JsonNames("page_size") val pageSize: Int
)

/** Result of GET /admin/users (includes flag when API returns 404 — old server image). */
data class UsersPageLoad(
    val page: PagedUsersDto,
    val endpointMissing: Boolean = false
)

@Serializable
data class AdminOrderSummaryDto(
    val orderId: String,
    val lineCount: Int = 1,
    val productName: String,
    val productNameUr: String? = null,
    val brandCode: String? = null,
    val skuCode: String? = null,
    val quantity: Int,
    val totalPrice: String,
    val createdAtMs: Long,
    val status: String,
    val estimatedDelivery: String,
    val printCount: Int = 0,
    val printedAtMs: Long? = null,
)

@Serializable
data class PagedAdminOrdersDto(
    val items: List<AdminOrderSummaryDto>,
    val total: Long,
    val page: Int,
    val pageSize: Int
)

@Serializable
data class OrderLineItemDto(
    val productName: String,
    val productNameUr: String? = null,
    val brandCode: String? = null,
    val skuCode: String? = null,
    val quantity: Int,
    val totalPrice: String
)

@Serializable
data class OrderDetailDto(
    val orderId: String,
    val lines: List<OrderLineItemDto> = emptyList(),
    val productName: String,
    val productNameUr: String? = null,
    val brandCode: String? = null,
    val skuCode: String? = null,
    val quantity: Int,
    val totalPrice: String,
    val createdAtMs: Long,
    val estimatedDelivery: String,
    val status: String,
    val agentName: String? = null,
    val agentNameUr: String? = null,
    val agentContact: String? = null,
    val printCount: Int = 0,
    val printedAtMs: Long? = null,
)

@Serializable
data class PatchOrderStatusRequestDto(
    val status: String? = null,
    val estimatedDelivery: String? = null,
)

@Serializable
data class LoginRequestDto(val username: String, val password: String)

@Serializable
data class LoginResponseDto(
    val token: String,
    val username: String,
    val user_id: String
)

@Serializable
data class CreateUserRequestDto(
    val username: String,
    val password: String,
    val userType: String = "admin",
    val status: String = "active",
    val nameEn: String? = null,
    val nameUr: String? = null,
    val addressEn: String? = null,
    val mobileEn: String? = null,
    @JsonNames("brand_codes") val brandCodes: List<String> = emptyList(),
)

@Serializable
data class PatchUserRequestDto(
    val nameEn: String? = null,
    val nameUr: String? = null,
    val addressEn: String? = null,
    val mobileEn: String? = null,
    val userType: String? = null,
    val status: String? = null,
    val password: String? = null,
    @JsonNames("brand_codes") val brandCodes: List<String>? = null,
)

/** Body for POST/PATCH user save when proxies block `PATCH /admin/users/{uuid}`. */
@Serializable
data class UserPatchSaveRequestDto(
    @JsonNames("user_id") val userId: String,
    @JsonNames("name_en") val nameEn: String? = null,
    @JsonNames("name_ur") val nameUr: String? = null,
    @JsonNames("address_en") val addressEn: String? = null,
    @JsonNames("mobile_en") val mobileEn: String? = null,
    @JsonNames("user_type") val userType: String? = null,
    val status: String? = null,
    val password: String? = null,
    @JsonNames("brand_codes") val brandCodes: List<String>? = null,
)

@Serializable
data class CreateUserResponseDto(val userId: String)

@Serializable
data class UserBrandsResponseDto(val brandCodes: List<String> = emptyList())

/** Local UI state for the create-user form (not sent to API as-is). */
data class NewUserFormState(
    val username: String = "",
    val password: String = "",
    val userType: String = "admin",
    val status: String = "active",
    val nameEn: String = "",
    val nameUr: String = "",
    val addressEn: String = "",
    val mobileEn: String = "",
    val selectedBrandCodes: List<String> = emptyList(),
)

/** Local UI state for editing an existing user. */
data class EditUserFormState(
    val userId: String = "",
    val username: String = "",
    val userType: String = "admin",
    val status: String = "active",
    val nameEn: String = "",
    val nameUr: String = "",
    val addressEn: String = "",
    val mobileEn: String = "",
    val passwordNew: String = "",
    val selectedBrandCodes: List<String> = emptyList(),
)

@Serializable
data class VariantDetailDto(
    val variantId: String,
    val skuCode: String,
    val legacyId: String? = null,
    val specs: Map<String, String>,
    val stock: Int,
    val nameUr: String? = null,
    val isActive: Boolean = true,
    /** Short readable label from API (brand · cast · size · mechanism · grade). */
    val displayName: String? = null,
)

@Serializable
data class AdminVariantSearchRowDto(
    val variantId: String,
    val skuCode: String,
    val displayName: String,
    val nameUr: String? = null,
    val stock: Int = 0,
    val specs: Map<String, String> = emptyMap(),
    val productId: String,
    val productName: String,
    val productNameUr: String? = null,
    val lineName: String = "",
    val sectionName: String = "",
)

@Serializable
data class PagedAdminVariantsDto(
    val items: List<AdminVariantSearchRowDto>,
    val total: Long,
    val page: Int,
    @JsonNames("page_size") val pageSize: Int,
)

@Serializable
data class CreateProductRequestDto(
    val productLineId: String,
    val catalogSectionId: String,
    val name: String,
    val nameUr: String? = null,
    val castIds: List<String> = emptyList(),
    val brandCodes: List<String> = emptyList(),
    val defaultSizes: List<String> = emptyList(),
    val defaultMechanisms: List<String> = emptyList(),
    val defaultWheelSizes: List<String> = emptyList(),
    val defaultGrades: List<String> = emptyList(),
    val basePrice: String? = null,
    val costPrice: String? = null,
)

@Serializable
data class AdminCastOptionDto(
    val id: String,
    val code: String,
    val name: String,
)

@Serializable
data class AdminBrandOptionDto(
    val code: String,
    val name: String? = null,
)

@Serializable
data class RegisterPickerValueRequestDto(
    val attrKey: String,
    val attrValue: String,
)

@Serializable
data class RegisterPickerValueResponseDto(val created: Boolean)

@Serializable
data class CreateCastRequestDto(
    val productLineId: String,
    val code: String,
    val name: String,
    val nameUr: String? = null,
)

@Serializable
data class CreateProductResponseDto(val productId: String)

@Serializable
data class UpdateProductRequestDto(
    val productId: String,
    val productLineId: String,
    val catalogSectionId: String,
    val name: String,
    val nameUr: String? = null,
    val castIds: List<String> = emptyList(),
    val brandCodes: List<String> = emptyList(),
    val defaultSizes: List<String> = emptyList(),
    val defaultMechanisms: List<String> = emptyList(),
    val defaultWheelSizes: List<String> = emptyList(),
    val defaultGrades: List<String> = emptyList(),
)

@Serializable
data class ProductModifiersDto(
    val sizes: List<String> = emptyList(),
    val mechanisms: List<String> = emptyList(),
    val grades: List<String> = emptyList(),
    val wheelSizes: List<String> = emptyList(),
    val brandCodes: List<String> = emptyList(),
    val castIds: List<String> = emptyList(),
)

@Serializable
data class ActivateProductRequestDto(val productId: String)

@Serializable
data class PatchVariantRequestDto(
    val legacyId: String? = null,
    val stock: Int? = null,
    /** Sent on every save from admin UI; omit on older clients (no change to DB). */
    val nameUr: String? = null,
    val isActive: Boolean? = null,
)

@Serializable
data class PatchModifierRequestDto(
    val priceImpact: String,
    val attrValueUr: String? = null,
    val attrKey: String? = null,
    val attrValue: String? = null,
)

@Serializable
data class ModifierPatchSaveRequestDto(
    val modifierId: String,
    val priceImpact: String,
    val attrValueUr: String? = null,
    val attrKey: String? = null,
    val attrValue: String? = null,
)

@Serializable
data class CreateModifierRequestDto(
    val attrKey: String,
    val attrValue: String,
    val priceImpact: String,
    val attrValueUr: String? = null,
)

@Serializable
data class CreateModifierResponseDto(
    @SerialName("modifierId")
    val modifierId: String
)

@Serializable
data class DeleteModifierRequestDto(
    val modifierId: String,
)

@Serializable
data class BoolResultDto(
    val updated: Boolean? = null,
    val archived: Boolean? = null,
    val deleted: Boolean? = null
)

@Serializable
data class ProductLineDto(
    val id: String,
    val name: String
)

@Serializable
data class CatalogSectionDto(
    val id: String,
    val code: String,
    val name: String? = null
)

@Serializable
data class PickerValuesDto(
    val values: List<String>
)
